import React, { useState, useEffect, useRef } from "react";

interface StaggeredFadeInProps {
  children: React.ReactNode;
  baseDelay?: number;
  staggerDelay?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
}

const StaggeredFadeIn: React.FC<StaggeredFadeInProps> = ({
  children: childrenProp,
  baseDelay = 0,
  staggerDelay = 200,
  direction = 'down'
}) => {
  const [visibleChildren, setVisibleChildren] = useState<{[key: number]: boolean}>({});
  const children = React.Children.toArray(childrenProp);
  const animationFrameRef = useRef<number>();
  const startTimeRef = useRef<number>(0);
  const isAnimatingRef = useRef<boolean>(false);

  useEffect(() => {
    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Reset state
    setVisibleChildren({});
    startTimeRef.current = performance.now();
    isAnimatingRef.current = true;

    const animate = (currentTime: number) => {
      if (!isAnimatingRef.current) return;

      const elapsed = currentTime - startTimeRef.current;
      let allVisible = true;

      children.forEach((_, index) => {
        const targetTime = baseDelay + (index * staggerDelay);
        
        if (elapsed >= targetTime && !visibleChildren[index]) {
          setVisibleChildren(prev => ({
            ...prev,
            [index]: true
          }));
        }
        
        if (elapsed < targetTime) {
          allVisible = false;
        }
      });

      // Continue animation if not all children are visible
      if (!allVisible) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        isAnimatingRef.current = false;
      }
    };

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isAnimatingRef.current) {
        // Reset and restart animation when tab becomes visible
        const currentTime = performance.now();
        const elapsed = currentTime - startTimeRef.current;
        
        // Check if we need to catch up on any missed animations
        children.forEach((_, index) => {
          const targetTime = baseDelay + (index * staggerDelay);
          if (elapsed >= targetTime && !visibleChildren[index]) {
            setVisibleChildren(prev => ({
              ...prev,
              [index]: true
            }));
          }
        });

        // Continue animation if needed
        const hasInvisibleChildren = children.some((_, index) => !visibleChildren[index]);
        if (hasInvisibleChildren) {
          isAnimatingRef.current = true;
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      }
    };

    // Start initial animation
    animationFrameRef.current = requestAnimationFrame(animate);

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      isAnimatingRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [baseDelay, staggerDelay, children.length]);

  const getTransform = (index: number) => {
    const transformMap = {
      'down': visibleChildren[index] ? 'translateY(0)' : 'translateY(50px)',
      'up': visibleChildren[index] ? 'translateY(0)' : 'translateY(-50px)',
      'left': visibleChildren[index] ? 'translateX(0)' : 'translateX(-50px)',
      'right': visibleChildren[index] ? 'translateX(0)' : 'translateX(50px)'
    };
    return transformMap[direction];
  };

  return (
    <>
      {children.map((child, index) => (
        <div 
          key={index} 
          className="transition-all duration-1000 ease-in-out"
          style={{
            opacity: visibleChildren[index] ? 1 : 0,
            transform: getTransform(index)
          }}
        >
          {child}
        </div>
      ))}
    </>
  );
};




export default StaggeredFadeIn;